import { FILTER_GROUPS } from "config/filters";

import LANDING_PAGE_QUERY from "./landingPageQuery";

export { LANDING_PAGE_QUERY };

// subPageType (the gateway token, see below) -> the filter option's key
// (config/filters.js), so an ALP's own defining filter can be matched against
// the checkbox options in Redux - mirrors nearMeKeywords.js's
// amenitySlugToFilterValue/getNMPBaseFilters for NMPs.
const ALP_BASE_FILTER_BY_SUBPAGE_TYPE = {
	"climate-controlled": {
		name: FILTER_GROUPS.AMENITIES,
		value: "climateControlled"
	},
	"24-hour-access": {
		name: FILTER_GROUPS.AMENITIES,
		value: "twentyFourHourAccess"
	},
	"drive-up-self-storage": {
		name: FILTER_GROUPS.AMENITIES,
		value: "driveUp"
	},
	electricity: { name: FILTER_GROUPS.AMENITIES, value: "power" },
	"free-truck-rental": {
		name: FILTER_GROUPS.AMENITIES,
		value: "freeTruckRental"
	},
	"wine-storage": { name: FILTER_GROUPS.AMENITIES, value: "onlyWine" },
	// The gateway can echo "mini" as the overlapping numeric bucket below
	// instead of "mini" itself - see search.js's getSearchPage.fulfilled.
	"mini-self-storage": {
		name: FILTER_GROUPS.UNIT_SIZE,
		value: "mini",
		collidesWith: "0-25"
	},
	"self-storage-lockers": {
		name: FILTER_GROUPS.UNIT_SIZE,
		value: "onlyLocker"
	},
	"5x5-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "0-25" },
	"5x10-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "26-60" },
	"5x15-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "61-75" },
	"10x10-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "76-100" },
	"10x15-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "101-150" },
	"10x20-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "151-200" },
	"10x25-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "201-250" },
	"10x30-storage": { name: FILTER_GROUPS.UNIT_SIZE, value: "251-300" },
	boat: { name: FILTER_GROUPS.VEHICLE_STORAGE, value: "boat-storage" },
	car: { name: FILTER_GROUPS.VEHICLE_STORAGE, value: "car-storage" },
	rv: { name: FILTER_GROUPS.VEHICLE_STORAGE, value: "rv-storage" }
};

// Returns the ALP's own defining filter as [{ name, value }], or [] for the
// plain city page/an unmapped subPageType. Used by applySearchClient to detect
// when the user has unchecked the filter that defines the page, so Apply can
// send them to the city's landing page instead - same idea as
// getNMPBaseFilters, applied to amenityLandingPage.
export function getALPBaseFilters(subPageType) {
	const base = ALP_BASE_FILTER_BY_SUBPAGE_TYPE[subPageType];
	return base ? [base] : [];
}

// subPageType -> curated breadcrumb label, mirroring nearMeKeywords.js's
// breadcrumbLabel. Kept in sync with storageMap.js's labels where one exists.
const ALP_BREADCRUMB_LABEL_BY_SUBPAGE_TYPE = {
	"climate-controlled": "Climate Controlled Storage Units",
	"24-hour-access": "24 Hour Storage Units",
	"drive-up-self-storage": "Drive Up Storage Units",
	electricity: "Storage Units with Electricity",
	"free-truck-rental": "Free Truck Rental Units",
	"wine-storage": "Wine Storage Units",
	"mini-self-storage": "Mini Storage Units",
	"self-storage-lockers": "Storage Lockers",
	"5x5-storage": "5x5 Storage Units",
	"5x10-storage": "5x10 Storage Units",
	"5x15-storage": "5x15 Storage Units",
	"10x10-storage": "10x10 Storage Units",
	"10x15-storage": "10x15 Storage Units",
	"10x20-storage": "10x20 Storage Units",
	"10x25-storage": "10x25 Storage Units",
	"10x30-storage": "10x30 Storage Units",
	boat: "Boat Storage Units",
	car: "Car Storage Units",
	rv: "RV Storage Units"
};

// Returns the static breadcrumb label for an ALP subPageType, or null if unknown.
export function getALPBreadcrumbLabel(subPageType) {
	return ALP_BREADCRUMB_LABEL_BY_SUBPAGE_TYPE[subPageType] || null;
}

export default function buildLandingQuery(match, queryParams, landingType) {
	const { city, state } = match.params;
	const location = `${city}, ${state}`;
	const pageType =
		landingType === "city" ? "cityLandingPage" : "amenityLandingPage"; // old LP types are needed for SEO service
	let query = {
		...queryParams,
		city,
		state,
		location,
		pageType
	};

	if (landingType === "amenity") {
		const { amenity: slug } = match.params;
		const mapped = LANDING_PAGE_QUERY[slug];

		// An unmapped slug would otherwise render a page identical to the plain
		// city page - a silent generic result rather than a visible failure. Since
		// routes.js is generated from this map, reaching here with an unknown slug
		// means routes.js and this map have drifted.
		if (!mapped) {
			return query;
		}

		// subPageType is what the gateway keys its content-template lookup on
		// (SiteContentClient.getContentTemplateId), so it tracks the gateway token
		// (or vehicle name) rather than the URL slug.
		query = {
			...query,
			...mapped,
			subPageType: mapped.amenity || mapped.vehicle
		};
	}

	return query;
}
